import { ReactNode, useState } from 'react';

import { Button } from '@components/button';
import { Card } from '@components/card';
import { IconForm } from '@components/icons/form';
import { SearchBox } from '@components/search-box';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';

type TemplateBase = { shortId: string; name: string; description: string | null };

type TemplateCardProps = {
  selectedId?: string;
  onSelect(newTemplateId: string): void;
  templates: Array<TemplateBase>;
  noResults: (search: string) => ReactNode;
  actionButton?: ReactNode;
};
export const TemplateCardList = ({
  noResults,
  templates,
  selectedId,
  onSelect,
  actionButton,
}: TemplateCardProps) => {
  const { t } = useScopedTranslate(locale);
  const [search, setSearch] = useState('');
  const filterableSearch = search.toLowerCase();
  const filteredList = templates.filter(
    (template) =>
      template.name.toLowerCase().includes(filterableSearch) ||
      template.description?.toLowerCase().includes(filterableSearch)
  );

  return (
    <Stack space={4}>
      <Stack orientation="horizontal" space={2}>
        <div style={{ flexGrow: 1 }}>
          <SearchBox onChange={setSearch} placeholder={t('search')} />
        </div>
        {actionButton}
      </Stack>
      {(templates.length === 0 || filteredList.length === 0) && noResults(search)}

      <Stack space={2}>
        {filteredList.map((template) => {
          const isSelected = selectedId === template.shortId;

          return (
            <Card
              variant={isSelected ? 'gray' : 'default'}
              key={template.shortId}
              spaceX={4}
              spaceY={2}
            >
              <Stack orientation="horizontal" justify="space-between">
                <Stack align="center" space={3} orientation="horizontal">
                  <IconForm />
                  <div>
                    <Text font="semibold14" text={template.name} />
                    <Text font="regular12" text={template.description} />
                  </div>
                </Stack>
                <Stack space={3} orientation="horizontal" align="center">
                  <Button
                    onClick={() => onSelect(template.shortId)}
                    variant="ghost"
                    text={isSelected ? t('selected') : t('select')}
                    isDisabled={isSelected}
                  />
                </Stack>
              </Stack>
            </Card>
          );
        })}
      </Stack>
    </Stack>
  );
};

const locale = {
  en: {
    select: 'Select',
    selected: 'Selected',
    search: 'Search',
  },
};
